/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import { MPL_BUBBLEGUM_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';
import {
  getDecompressibleStateDecoder,
  getDecompressibleStateEncoder,
  type DecompressibleState,
  type DecompressibleStateArgs,
} from '../types';

export const SET_DECOMPRESSIBLE_STATE_DISCRIMINATOR = new Uint8Array([
  82, 104, 152, 6, 149, 111, 100, 13,
]);

export function getSetDecompressibleStateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SET_DECOMPRESSIBLE_STATE_DISCRIMINATOR
  );
}

export type SetDecompressibleStateInstruction<
  TProgram extends string = typeof MPL_BUBBLEGUM_PROGRAM_ADDRESS,
  TAccountTreeConfig extends string | AccountMeta<string> = string,
  TAccountTreeCreator extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountTreeConfig extends string
        ? WritableAccount<TAccountTreeConfig>
        : TAccountTreeConfig,
      TAccountTreeCreator extends string
        ? ReadonlySignerAccount<TAccountTreeCreator> &
            AccountSignerMeta<TAccountTreeCreator>
        : TAccountTreeCreator,
      ...TRemainingAccounts,
    ]
  >;

export type SetDecompressibleStateInstructionData = {
  discriminator: ReadonlyUint8Array;
  decompressableState: DecompressibleState;
};

export type SetDecompressibleStateInstructionDataArgs = {
  decompressableState: DecompressibleStateArgs;
};

export function getSetDecompressibleStateInstructionDataEncoder(): FixedSizeEncoder<SetDecompressibleStateInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['decompressableState', getDecompressibleStateEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: SET_DECOMPRESSIBLE_STATE_DISCRIMINATOR,
    })
  );
}

export function getSetDecompressibleStateInstructionDataDecoder(): FixedSizeDecoder<SetDecompressibleStateInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['decompressableState', getDecompressibleStateDecoder()],
  ]);
}

export function getSetDecompressibleStateInstructionDataCodec(): FixedSizeCodec<
  SetDecompressibleStateInstructionDataArgs,
  SetDecompressibleStateInstructionData
> {
  return combineCodec(
    getSetDecompressibleStateInstructionDataEncoder(),
    getSetDecompressibleStateInstructionDataDecoder()
  );
}

export type SetDecompressibleStateInput<
  TAccountTreeConfig extends string = string,
  TAccountTreeCreator extends string = string,
> = {
  treeConfig: Address<TAccountTreeConfig>;
  treeCreator: TransactionSigner<TAccountTreeCreator>;
  decompressableState: SetDecompressibleStateInstructionDataArgs['decompressableState'];
};

export function getSetDecompressibleStateInstruction<
  TAccountTreeConfig extends string,
  TAccountTreeCreator extends string,
  TProgramAddress extends Address = typeof MPL_BUBBLEGUM_PROGRAM_ADDRESS,
>(
  input: SetDecompressibleStateInput<TAccountTreeConfig, TAccountTreeCreator>,
  config?: { programAddress?: TProgramAddress }
): SetDecompressibleStateInstruction<
  TProgramAddress,
  TAccountTreeConfig,
  TAccountTreeCreator
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? MPL_BUBBLEGUM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    treeConfig: { value: input.treeConfig ?? null, isWritable: true },
    treeCreator: { value: input.treeCreator ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.treeConfig),
      getAccountMeta(accounts.treeCreator),
    ],
    data: getSetDecompressibleStateInstructionDataEncoder().encode(
      args as SetDecompressibleStateInstructionDataArgs
    ),
    programAddress,
  } as SetDecompressibleStateInstruction<
    TProgramAddress,
    TAccountTreeConfig,
    TAccountTreeCreator
  >);
}

export type ParsedSetDecompressibleStateInstruction<
  TProgram extends string = typeof MPL_BUBBLEGUM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    treeConfig: TAccountMetas[0];
    treeCreator: TAccountMetas[1];
  };
  data: SetDecompressibleStateInstructionData;
};

export function parseSetDecompressibleStateInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedSetDecompressibleStateInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { treeConfig: getNextAccount(), treeCreator: getNextAccount() },
    data: getSetDecompressibleStateInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
